import { toDateKey } from '@apps/web/lib/date_utils';
import { type Organization } from '@libraries/typescript/api_client';

/** Target length of the training period, used for the progress bar. */
export const TRAINING_PERIOD_DAYS = 21;

/**
 * Training mode is a sub-state of an IMPLEMENTING organization: on when the
 * org is implementing on the customer lifecycle, training mode is enabled,
 * and a window start date exists. Mirrors the backend org_in_training_mode
 * predicate — all three conditions. The enabled flag is separate from
 * training_mode_started_at, so toggling off then on does not restart the
 * 21-day window (the start date is kept while enabled is false).
 */
export function isInTrainingMode(
    organization:
        | Pick<
              Organization,
              | 'customer_lifecycle_stage'
              | 'training_mode_enabled'
              | 'training_mode_started_at'
          >
        | null
        | undefined,
): boolean {
    // Boolean() rather than a null check: the field is optional in some
    // generated schemas, and undefined must read as "no start date" too.
    return (
        organization?.customer_lifecycle_stage === 'implementing' &&
        organization.training_mode_enabled &&
        Boolean(organization.training_mode_started_at)
    );
}

/**
 * How far through the 21-day window the organization is, as a 0-100 percent
 * for the progress bar. Clamped: training does not stop at day 21, so a
 * long-running window would otherwise overflow the bar.
 */
export function trainingProgressPercent(trainingDay: number): number {
    return Math.min(100, (trainingDay / TRAINING_PERIOD_DAYS) * 100);
}

/**
 * The day number to display alongside "of {TRAINING_PERIOD_DAYS}". Training
 * continues past day 21, so the raw day count can exceed the window length —
 * clamp it so labels never read like "Day 48 of 21".
 */
export function clampedTrainingDay(day: number): number {
    return Math.min(day, TRAINING_PERIOD_DAYS);
}

/**
 * 1-based day count since training mode started, using calendar days in
 * the organization's timezone (the day training starts is day 1).
 */
export function trainingDayNumber(startedAtIso: string, tz: string): number {
    const msPerDay = 24 * 60 * 60 * 1000;
    const startKey = toDateKey(new Date(startedAtIso), tz);
    const todayKey = toDateKey(new Date(), tz);
    const elapsedDays = Math.round(
        (Date.parse(todayKey) - Date.parse(startKey)) / msPerDay,
    );
    return elapsedDays + 1;
}
